import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { UseFormGetValues } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import {
  getConstructionDetails,
  getAllConstructionList
} from '../../apicalls/construction';
import {
  exportSiteProcessGanttExcel,
  exportSiteProcessListExcel,
  getSiteProcessList
} from '../../apicalls/process';
import { getSiteProcessHistory } from '../../apicalls/processHistory';
import { SITE_PROCESS_DISPLAY_MODE } from '../../const/ProcessConst';
import {
  TAttachableObject,
  TConstructionMinimal,
  TSelectItem
} from '../../types/common';
import {
  TSiteProcessHistoryData,
  TSiteProcessListApiResponse,
  TSiteProcessPageLocationState
} from '../../types/process';
import { convertFormDateToApiDate } from '../../utils';
import { checkResData, formatDatetime } from '../../utils/common';
import { log } from '../../utils';
import { parseNumber } from '../../utils/processAndTaskFormUtils';
import { getConstructionsSelectBoxData } from '../../utils/common';

export type Props = {
  getValues: UseFormGetValues<any>;
};

export const useHooks = (props: Props) => {
  const { getValues } = props;

  // snackbar
  const { enqueueSnackbar } = useSnackbar();

  // location.stateから工事IDを取得
  const location = useLocation();
  const state = location.state as TSiteProcessPageLocationState;
  const query = new URLSearchParams(useLocation().search);
  const constructIdQueryDef = parseNumber(query.get('constructId')) ?? null;
  const constructIdStateDef = state?.constructId ? state.constructId : 0;
  const constructNameStateDef = state?.name ? state.name : '';
  const listMode = state?.listMode || false;

  const constructListDefaultVal = {
    value: 0,
    label: '--'
  };

  // 工事選択リスト
  const [constructList, setConstructList] = useState<TSelectItem[]>([
    constructListDefaultVal
  ]);

  // 工事選択リストに表示する値
  const [constructListVal, setConstructListVal] = useState<TSelectItem>(
    constructListDefaultVal
  );

  // {工事ID:顧客名}のオブジェクト
  // 工事選択の度に顧客名取得のためにAPIを実行しなくてよいように
  // 初回レンダリングの工事リスト取得に各工事の顧客名を保持しておく
  const [customerNameList, setCustomerNameList] = useState<TAttachableObject>(
    {}
  );

  // 選択した工事の顧客名
  const [customerName, setCustomerName] = useState('');

  // ページ番号
  const [currentPage, setCurrentPage] = useState(1);

  // 選択した工事ID
  const [constructId, setConstructId] = useState(0);

  // 最終更新時刻、更新者
  const [lastUpdate, setLastUpdate] = useState({
    at: '',
    by: ''
  });

  // タスク件数
  const [siteTaskCounts, setSiteTaskCounts] = useState({
    task: 0,
    uncomplete: 0,
    alert: 0
  });

  // 表示モード（リスト/ガント）
  const [displayMode, setDisplayMode] = useState(
    SITE_PROCESS_DISPLAY_MODE.gantt
  );

  // 表示モード（工事に紐づく工程が0件の場合）
  const [hasSiteProcess, setHasSiteProcess] = useState(false);
  const [hasExpectedDays, setHasExpectedDays] = useState(false);

  // 工程追加モーダルの表示制御
  const [isOpenAdd, setIsOpenAdd] = useState(false);

  // ガントチャート用工程追加モーダルの表示制御
  const [isOpenAddOfGantt, setIsOpenAddOfGantt] = useState(false);
  const [parentSiteProcessIdOfGanttAdd, setParentSiteProcessIdOfGanttAdd] =
    useState<number | undefined>(undefined);

  // 再レンダリングのトリガ用state
  const [renderSiteProcessDataTrigger, setRenderSiteProcessDataTrigger] =
    useState(false);
  //工程検索のフォーム入力値
  const [userInput, setUserInput] = useState('');

  // 再レンダリング
  const reRenderSiteProcessData = () => {
    setRenderSiteProcessDataTrigger(prev => !prev);

    // 工程一覧を取得してタスク件数をセット＆工程の有無をチェック
    getSiteProcessList(constructId, false, '', '', 1, 1).then(
      (resData: any) => {
        checkResData(resData, enqueueSnackbar).then((data: any) => {
          if (data) {
            const siteProcessList: TSiteProcessListApiResponse = data;
            if (siteProcessList) {
              setSiteTaskCounts({
                task: siteProcessList.site_process_summary.site_task_count,
                uncomplete:
                  siteProcessList.site_process_summary
                    .site_task_uncompleted_count,
                alert:
                  siteProcessList.site_process_summary.site_task_alert_count
              });
              // 工程の有無をチェック（工事に紐づく工程が0件の場合）
              if (siteProcessList.data.length === 0) {
                setHasSiteProcess(false);
              } else {
                setHasSiteProcess(true);
              }
              // 総工事日数
              setTotalSiteProcessDays(
                siteProcessList.site_process_summary.total_site_process_days
              );
            }
          }
        });
      }
    );

    // 履歴から最終更新日時、更新担当者を取得
    getSiteProcessHistory(constructId, undefined, undefined, 1, 1).then(
      (resData: any) => {
        checkResData(resData, enqueueSnackbar).then((data: any) => {
          if (data) {
            const lastHistoryData: TSiteProcessHistoryData = data.data[0];
            setLastUpdate({
              at: lastHistoryData?.added_at ?? '',
              by: lastHistoryData?.added_user_name ?? ''
            });
          }
        });
      }
    );
  };

  // ガントチャートの日付のFROM-TO
  const [ganttFromTo, setGanttFromTo] = useState({
    from: '',
    to: ''
  });

  // ガントチャートのローディング中判定
  // 「工程を追加」ボタンの制御がいるため親から渡す
  const [isLoadingGantt, setIsLoadingGantt] = useState(false);

  const [isSearching, setIsSearching] = useState(false);

  const [isDownloading, setIsDownloading] = useState(false);

  const [isOpenCopy, setIsOpenCopy] = useState(false);

  const handleOnClickCopy = () => {
    setIsOpenCopy(true);
  };

  const [isOpenEditPeriod, setIsOpenEditPeriod] = useState(false);

  const handleOnClickEditPeriod = () => {
    setIsOpenEditPeriod(true);
  };

  const [isOpenAllDelete, setIsOpenAllDelete] = useState(false);

  const handleOnClickAllDelete = () => {
    setIsOpenAllDelete(true);
  };

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClickMoreVert = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClosePopover = () => {
    setAnchorEl(null);
  };
  const isOpenPopover = Boolean(anchorEl);
  const popoverId = isOpenPopover ? 'simple-popover' : undefined;

  // 総工事日数・着工日・竣工日をセット
  const [totalSiteProcessDays, setTotalSiteProcessDays] = useState<number>();
  const [constructStartDay, setConstructStartDay] = useState<string>();
  const [constructEndDay, setConstructEndDay] = useState<string>();

  // 工事選択時のハンドラ
  const renderCurrentConstructView = () => {
    let currentConstructId = 0;

    // 選択欄から工事を指定した場合
    if (getValues('construct_id')) {
      currentConstructId = getValues('construct_id').value as number;
    } else {
      // 他ページから遷移する際に工事IDを指定した場合
      currentConstructId = constructIdStateDef;
    }

    // 未選択が設定されている場合はデフォルト値を設定
    if (currentConstructId === 0) {
      setConstructListVal(
        userInput ? { value: 0, label: userInput } : constructListVal
      );
      return;
    }

    if (currentConstructId) {
      setConstructId(currentConstructId);
      setCustomerName(customerNameList[String(currentConstructId)]);
      setCurrentPage(1);

      setHasSiteProcess(false);
      setLastUpdate({
        at: '',
        by: ''
      });
      setSiteTaskCounts({
        task: 0,
        uncomplete: 0,
        alert: 0
      });
      setTotalSiteProcessDays(0);
      setConstructStartDay('--');
      setConstructEndDay('--');

      if (currentConstructId > 0) {
        // 着工日・竣工日を取得
        getConstructionDetails(currentConstructId).then((resData: any) => {
          checkResData(resData, enqueueSnackbar).then((data: any) => {
            if (data) {
              setConstructStartDay(
                data.data.start_date_str ? data.data.start_date_str : '--'
              );
              setConstructEndDay(
                data.data.end_date_str ? data.data.end_date_str : '--'
              );
              if (
                data.data.expected_contract_date &&
                data.data.expected_end_date
              ) {
                setHasExpectedDays(true);
              } else {
                setHasExpectedDays(false);
              }
            }
          });
        });
        setIsSearching(true);
        // 履歴から最終更新日時、更新担当者を取得
        getSiteProcessHistory(
          currentConstructId,
          undefined,
          undefined,
          1,
          1
        ).then((resData: any) => {
          checkResData(resData, enqueueSnackbar).then((data: any) => {
            if (data) {
              const lastHistoryData: TSiteProcessHistoryData = data.data[0];
              setLastUpdate({
                at: lastHistoryData?.added_at ?? '',
                by: lastHistoryData?.added_user_name ?? ''
              });
            }
          });
        });

        // 工程一覧を取得してタスク件数をセット＆工程の有無をチェック
        getSiteProcessList(currentConstructId, false, '', '', 1, 1).then(
          (resData: any) => {
            checkResData(resData, enqueueSnackbar).then((data: any) => {
              if (data) {
                const siteProcessList: TSiteProcessListApiResponse = data;
                if (siteProcessList) {
                  setSiteTaskCounts({
                    task: siteProcessList.site_process_summary.site_task_count,
                    uncomplete:
                      siteProcessList.site_process_summary
                        .site_task_uncompleted_count,
                    alert:
                      siteProcessList.site_process_summary.site_task_alert_count
                  });
                  // 工程の有無をチェック（工事に紐づく工程が0件の場合）
                  if (siteProcessList.data.length === 0) {
                    setHasSiteProcess(false);
                  } else {
                    setHasSiteProcess(true);
                  }
                  // 総工事日数
                  setTotalSiteProcessDays(
                    siteProcessList.site_process_summary.total_site_process_days
                  );
                }
              }
              setIsSearching(false);
            });
          }
        );
      }

      // 工事IDが該当する工事を工事選択プルダウンにセット
      constructList.forEach((construct: { value: number; label: string }) => {
        if (construct.value === currentConstructId) {
          setConstructListVal(construct);
        }
      });
    } else {
      setConstructId(0);
      setCustomerName('');
    }
  };

  // 「ガントチャート形式で表示」クリック時のハンドラ
  const handleOnClickGantt = () => {
    setDisplayMode(SITE_PROCESS_DISPLAY_MODE.gantt);
    setCurrentPage(1);
  };

  // 「リスト形式で表示」クリック時のハンドラ
  const handleOnClickList = () => {
    setDisplayMode(SITE_PROCESS_DISPLAY_MODE.list);
  };
  // 「更新履歴」クリック時のハンドラ
  const [isOpenSiteProcessHistory, setIsOpenSiteProcessHistory] =
    useState(false);
  const handleOnClickHistory = () => {
    setIsOpenSiteProcessHistory(true);
  };

  // 「工程の追加」クリック時のハンドラ
  const handleOnClickAdd = () => {
    setIsOpenAdd(true);
  };

  // ガントチャート形式時の「工程の追加」クリック時のハンドラ
  const handleOnClickAddOfGantt = () => {
    setParentSiteProcessIdOfGanttAdd(undefined);
    setIsOpenAddOfGantt(true);
  };

  // Excelダウンロード処理共通部分
  const downloadExcel = (data: any, fileName: string) => {
    // ファイルへのリンク(aタグ)を作成してclickイベントでダウンロードさせる
    const url = URL.createObjectURL(
      new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
    link.parentNode?.removeChild(link);
  };

  // ガントチャート形式Excelダウンロード
  const downloadProcessGanttExcel = () => {
    setIsDownloading(true);
    exportSiteProcessGanttExcel(
      constructId,
      convertFormDateToApiDate(ganttFromTo.from),
      convertFormDateToApiDate(ganttFromTo.to)
    ).then((resData: any) => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          const currentDatetime = formatDatetime(new Date(), 'YYYYMMDD');
          const fileName = `process_gantt_${currentDatetime}.xlsx`;

          downloadExcel(data, fileName);

          // ログ出力
          log.info(
            `exportExcel: process view mode: ganttchart ${
              ganttFromTo.from + '~' + ganttFromTo.to
            }`
          );
        }
        setIsDownloading(false);
      });
    });
  };

  // リスト形式Excelダウンロード
  const downloadSiteProcessListExcel = () => {
    setIsDownloading(true);
    exportSiteProcessListExcel(constructId).then((resData: any) => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          const currentDatetime = formatDatetime(new Date(), 'YYYYMMDD');
          const fileName = `process_list_${currentDatetime}.xlsx`;

          downloadExcel(data, fileName);

          // ログ出力
          log.info('exportExcel: process view mode: list');
        }
        setIsDownloading(false);
      });
    });
  };

  // 「Excel出力」クリック時のハンドラ
  const handleOnClickExportExcel = () => {
    if (displayMode === SITE_PROCESS_DISPLAY_MODE.gantt) {
      downloadProcessGanttExcel();
    } else if (displayMode === SITE_PROCESS_DISPLAY_MODE.list) {
      downloadSiteProcessListExcel();
    }
  };

  // 工事選択リスト設定（初回のみ）
  useEffect(() => {
    // 工事セレクトボックスを取得
    getConstructionsSelectBoxData(setConstructList, enqueueSnackbar, {
      value: 0,
      label: '--'
    });

    // リスト→タスク→リストに戻るための判定
    if (listMode) {
      setDisplayMode(SITE_PROCESS_DISPLAY_MODE.list);
    }

    getAllConstructionList().then((resData: any) => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          const resConstructList: TConstructionMinimal[] = data.data;
          if (resConstructList && resConstructList.length > 0) {
            const objCustomerName: TAttachableObject = {};
            resConstructList.forEach((construct: TConstructionMinimal) => {
              objCustomerName[String(construct.id)] = construct.customer_name;
            });
            setConstructId(constructIdQueryDef ?? constructIdStateDef);
            setCustomerNameList(objCustomerName);
          }
        }
      });
    });

    // 工事IDの初期値が指定されている場合は工程情報を取得する
    if (constructIdStateDef) {
      setConstructListVal({
        value: constructIdStateDef,
        label: constructNameStateDef
      });
      renderCurrentConstructView();
    }
  }, []);

  return {
    constructList,
    constructListVal,
    constructId,
    renderCurrentConstructView,
    currentPage,
    setCurrentPage,
    lastUpdate,
    displayMode,
    handleOnClickGantt,
    handleOnClickList,
    handleOnClickHistory,
    isOpenAdd,
    setIsOpenAdd,
    handleOnClickAdd,
    siteTaskCounts,
    isOpenSiteProcessHistory,
    setIsOpenSiteProcessHistory,
    handleOnClickExportExcel,
    renderSiteProcessDataTrigger,
    reRenderSiteProcessData,
    setGanttFromTo,
    constructStartDay,
    constructEndDay,
    customerName,
    isOpenAddOfGantt,
    setIsOpenAddOfGantt,
    parentSiteProcessIdOfGanttAdd,
    setParentSiteProcessIdOfGanttAdd,
    handleOnClickAddOfGantt,
    hasSiteProcess,
    hasExpectedDays,
    totalSiteProcessDays,
    anchorEl,
    handleClickMoreVert,
    handleClosePopover,
    isOpenPopover,
    popoverId,
    isSearching,
    isDownloading,
    isLoadingGantt,
    setIsLoadingGantt,
    isOpenCopy,
    setIsOpenCopy,
    handleOnClickCopy,
    isOpenEditPeriod,
    setIsOpenEditPeriod,
    handleOnClickEditPeriod,
    isOpenAllDelete,
    setIsOpenAllDelete,
    handleOnClickAllDelete,
    setHasExpectedDays,
    setHasSiteProcess
  };
};
